import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Images, Users } from "lucide-react";
import { formatDate, money } from "../lib/api";

const CoverVideo = ({ src }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { rootMargin: "120px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      src={src}
      muted
      loop
      playsInline
      preload="metadata"
      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
  );
};

export const WorkshopCard = ({ workshop, index, onBook, onView }) => {
  const slots = workshop.timeslots || [];
  const seatsLeft = workshop.total_seats_left ?? slots.reduce((a, s) => a + Math.max(0, s.capacity - s.booked), 0);
  const soldOut = seatsLeft === 0;
  const media = workshop.media?.length ? workshop.media : [{ kind: "image", url: workshop.image }];
  const cover = media[0];
  const hasGallery = (workshop.media?.length || 0) > 1 || media.some((m) => m.kind === "video");

  return (
    <motion.article
      data-testid={`workshop-card-${workshop._id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: (index % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="card-soft group overflow-hidden flex flex-col"
    >
      <div
        className={`relative h-60 sm:h-64 overflow-hidden ${hasGallery ? "cursor-pointer" : ""}`}
        onClick={hasGallery ? () => onView?.(workshop) : undefined}
        data-testid={`workshop-cover-${workshop._id}`}
      >
        {cover.kind === "video" ? (
          <CoverVideo src={cover.url} />
        ) : (
          <img
            src={cover.url}
            alt={workshop.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        <span className="absolute top-4 left-4 glass-chip rounded-full px-3.5 py-1.5 text-[10px] font-bold tracking-[0.14em] uppercase text-neutral-700">
          {workshop.theme}
        </span>
        <span className="absolute bottom-4 right-4 rounded-full bg-white/90 backdrop-blur px-4 py-1.5 font-display font-extrabold text-lg shadow-lg">
          {money(workshop.price_cents)}
        </span>
        {hasGallery && (
          <button
            data-testid={`workshop-view-media-${workshop._id}`}
            onClick={(e) => { e.stopPropagation(); onView?.(workshop); }}
            className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-[11px] font-bold shadow-lg hover:bg-white transition-colors"
          >
            <Images size={12} /> {media.length} photo{media.length > 1 ? "s" : ""}{media.some((m) => m.kind === "video") ? " & video" : ""}
          </button>
        )}
      </div>

      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-display font-extrabold text-xl sm:text-[22px] tracking-tight leading-snug">
          {workshop.title}
        </h3>
        <p className="mt-2.5 text-sm text-neutral-500 leading-relaxed line-clamp-2">
          {workshop.description}
        </p>

        <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-neutral-600">
          <CalendarDays size={15} className="text-[#1c1a17]" />
          {formatDate(workshop.date)}
          {workshop.dates?.length > 1 && (
            <span className="rounded-full bg-[#eae4da] px-2.5 py-0.5 text-[11px] font-bold text-neutral-600">
              + {workshop.dates.length - 1} more date{workshop.dates.length > 2 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {slots.map((s) => {
            const left = Math.max(0, s.capacity - s.booked);
            return (
              <span
                key={s.id}
                className={`rounded-full px-3 py-1 text-xs font-bold border ${
                  left === 0
                    ? "border-neutral-200 text-neutral-300 line-through"
                    : "border-neutral-200 bg-neutral-50 text-neutral-600"
                }`}
              >
                {s.label}
              </span>
            );
          })}
        </div>

        <div className="mt-5 pt-4 border-t border-black/[0.06] flex items-center justify-between gap-3">
          <span
            data-testid={`workshop-seats-left-${workshop._id}`}
            className={`inline-flex items-center gap-1.5 text-xs font-bold ${seatsLeft <= 5 && !soldOut ? "text-[#1c1a17]" : "text-neutral-400"}`}
          >
            <Users size={13} />
            {soldOut ? "Sold out" : `${seatsLeft} seats left`}
          </span>
          <button
            data-testid={`workshop-book-button-${workshop._id}`}
            onClick={() => onBook(workshop)}
            disabled={soldOut}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-300 ${
              soldOut ? "bg-neutral-100 text-neutral-400 cursor-not-allowed" : "btn-dark"
            }`}
          >
            {soldOut ? "Fully Booked" : "Book Your Spot"}
            {!soldOut && <ArrowRight size={14} />}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
